import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {IconButton} from '@mui/material';
import {MaterialReactTable} from 'material-react-table';
import moment from 'moment';
import {IoMdAddCircleOutline, IoMdCheckmarkCircle, IoMdCloseCircle} from 'react-icons/io';
import {MdInfo, MdOutlineForwardToInbox} from 'react-icons/md';
import {useModifiedMaterialReactTable} from '../../misc/modified_react_table';
import {useClientSubscriptionLookup} from '../../processes/all_client_subscription_lookup';
import {useClientSubscriptionCreateState} from '../../processes/client_subscription_create';
import {useClientLookup} from '../../processes/client/client';
import ClientRegistrationDialog from './ClientRegistrationDialog';
import SubscriptionsDialog from './SubscriptionsDialog';
import SubscriptionNotificationDialog from "./SubscriptionNotificationDialog";
import ManualReminderDialog from "./ManualReminderDialog";
import {RiNotification3Line} from "react-icons/ri";

export default function ClientSubscription() {
    const registrationDialog = useRef();
    const [viewingSubscriptions, setViewingSubscriptions] = useState(null);
    const [dialogKey, setDialogKey] = useState(0);
    const [availableCompanies, setAvailableCompanies] = useState([]);
    const [notifOpen, setNotifOpen] = useState(false);
    const [manualReminderFor, setManualReminderFor] = useState(null);
    const {isPending: clientRegistrationIsInProgress} = useClientSubscriptionCreateState();
    const {
        data: allClientSummaries,
        isLoading: isLoadingAllUserSummaries,
        isFetching: isFetchingAllUserSummaries,
        refetch: refetchAllClientSummaries
    } = useClientSubscriptionLookup();
    const {data: clients = []} = useClientLookup();

    useEffect(() => {
        if (Array.isArray(clients)) {
            const map = new Map();
            clients.forEach((item) => {
                if (item.clientId && item.clientName) map.set(item.clientId, {
                    id: item.clientId,
                    name: item.clientName
                });
            });
            setAvailableCompanies(Array.from(map.values()));
        }
    }, [clients]);

    const activeClientSubscriptions = useMemo(() => {
        if (!allClientSummaries?.length) return new Set();
        return new Set(
            allClientSummaries
                .filter((item) => item.isActive === 1)
                .map((item) => item.client?.id)
                .filter(Boolean)
        );
    }, [allClientSummaries]);

    const openRegistrationDialog = useCallback(() => registrationDialog.current?.showModal(), []);

    const handleRegistrationSuccess = useCallback(() => {
        refetchAllClientSummaries();
        setDialogKey((prev) => prev + 1);
    }, [refetchAllClientSummaries]);

    const columns = useMemo(() => ([
        {
            id: 'clientId',
            header: 'Client Id',
            accessorFn: (row) => row.client?.id || 'N/A',
            Cell: ({cell}) => <span className="tw-font-bold">...{String(cell.getValue() || '').slice(-5)}</span>
        },
        {
            id: 'clientName',
            header: 'Name',
            accessorFn: (row) => row.client?.name || 'N/A',
            Cell: ({cell}) => <span className="tw-font-bold">{cell.getValue()}</span>
        },
        {
            id: 'count',
            header: 'Subs Count',
            accessorFn: (row) => row.count || 0,
            Cell: ({cell}) => <span>{cell.getValue()}</span>
        },
        {
            id: 'start',
            header: 'Start Date',
            accessorFn: (row) => (row.start ? moment(row.start).format('DD MMM YYYY') : 'N/A')
        },
        {id: 'end', header: 'End Date', accessorFn: (row) => (row.end ? moment(row.end).format('DD MMM YYYY') : 'N/A')},
        {
            id: 'email', header: 'Reminder', accessorKey: 'reminders', Cell: ({cell}) => {
                const emails = Array.isArray(cell.getValue()) ? cell.getValue() : [];
                return emails.length ? (<div>{emails.map((e, i) => <div key={i}>{e}</div>)}</div>) : (<span>-</span>);
            }
        },
        {
            id: 'active',
            header: 'Active',
            accessorKey: 'isActive',
            muiTableBodyCellProps: {sx: {px: 0}},
            Cell: ({cell}) => (
                <div className="tw-flex tw-justify-center tw-items-center tw-w-full">
                    {cell.getValue() === 1 ? <IoMdCheckmarkCircle style={{color: '#4CAF50', fontSize: 20}}/> :
                        <IoMdCloseCircle style={{color: '#F44336', fontSize: 20}}/>}
                </div>
            )
        },
    ]), []);

    const table = useModifiedMaterialReactTable({
        data: allClientSummaries || [],
        columns,
        getRowId: (row) => row.id,
        enablePagination: true,
        enableRowActions: true,
        enableColumnActions: false,
        state: {
            isLoading: isLoadingAllUserSummaries,
            showProgressBars: clientRegistrationIsInProgress,
            isSaving: clientRegistrationIsInProgress,
        },
        renderTopToolbarCustomActions: () => (
            <span className="tw-flex-1 tw-flex tw-items-center tw-gap-1">
      <IconButton onClick={openRegistrationDialog} title="New Subscription">
        <IoMdAddCircleOutline className="tw-text-2xl"/>
      </IconButton>
      <IconButton onClick={() => setNotifOpen(true)} title="Today's Reminders">
        <RiNotification3Line className="tw-text-2xl"/>
      </IconButton>
    </span>
        ),
        renderRowActions: ({row}) => ([
            <IconButton key={`log-${row?.id}`} title="View Subscriptions"
                        onClick={() => setViewingSubscriptions(row.original)}>
                <MdInfo fontSize={'1.5rem'}/>
            </IconButton>,
            <IconButton
                key={`remind-${row?.id}`}
                title="Send Reminder"
                disabled={!row.original?.id}
                onClick={() => setManualReminderFor(row.original)}
            >
                <MdOutlineForwardToInbox fontSize={'1.5rem'}/>
            </IconButton>,
        ]),
    });

    // wire simple event bridge for delete to keep this page thin
    useEffect(() => {
        const handler = (e) => {
            const {id} = e.detail || {}; // subscription id
            // You already have a delete mutation hook in your original file.
            // We keep deletion inside SubscriptionsDialog and you can also choose
            // to lift it here and invalidate queries globally if preferred.
        };
        window.addEventListener('subscriptions:delete', handler);
        return () => window.removeEventListener('subscriptions:delete', handler);
    }, []);

    return (
        <div className="tw-flex-1 tw-flex tw-flex-col tw-items-stretch tw-p-7">
            <div
                className="tw-mb-4 tw-rounded-md tw-bg-yellow-100 tw-border tw-border-yellow-400 tw-text-yellow-800 tw-px-4 tw-py-2 tw-text-sm">
                <strong>PERHATIAN:</strong> Status aktif di <b>Client Subscription</b> berbeda dengan <b>Client
                Premium</b>. Pastikan keduanya selalu sinkron. Premium harus diatur manual di menu Client.
            </div>

            <MaterialReactTable table={table}/>

            <ClientRegistrationDialog
                key={dialogKey}
                dialog={registrationDialog}
                companies={availableCompanies}
                activeClientSubscriptions={activeClientSubscriptions}
                onSuccess={handleRegistrationSuccess}
            />

            {viewingSubscriptions && (
                <SubscriptionsDialog
                    subscription={viewingSubscriptions}
                    onClose={() => setViewingSubscriptions(null)}
                    refetchAllClientSummaries={refetchAllClientSummaries}
                />
            )}
            <SubscriptionNotificationDialog isOpen={notifOpen} onClose={() => setNotifOpen(false)}/>

            {manualReminderFor && (
                <ManualReminderDialog
                    subscription={manualReminderFor}
                    onClose={() => setManualReminderFor(null)}
                    onSent={() => refetchAllClientSummaries()}
                />
            )}
        </div>
    );
}
